import { useRef } from 'react';
import { useHorizontalOverflowIndicators } from 'src/modules/shared/hooks/use-horizontal-overflow-indicators';
import type { HorizontalOverflowStripProps } from 'src/modules/shared/types/horizontal-overflow-strip.type';
import { classes } from 'src/modules/shared/utils/styles.util';

/**
 * Displays horizontal content with edge indicators and optional keyboard access.
 * @param props - Strip content, presentation, and optional accessible focus target.
 * @returns A native scroll area, focusable when an accessible name is supplied.
 */
export const HorizontalOverflowStrip = ({
  children,
  className,
  contentClassName,
  threshold,
  watchValue,
  ariaLabel,
  scrollRef,
}: HorizontalOverflowStripProps) => {
  const localRef = useRef<HTMLDivElement>(null);
  const containerRef = scrollRef ?? localRef;
  const { showLeftOverflow, showRightOverflow } =
    useHorizontalOverflowIndicators({
      ref: containerRef,
      threshold,
      watchValue,
    });

  return (
    <div className={classes('relative min-w-0', className)}>
      {showLeftOverflow && (
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-6 bg-gradient-to-r from-white via-white/95 to-transparent shadow-[10px_0_16px_-14px_rgba(15,23,42,0.35)]" />
      )}

      {showRightOverflow && (
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-6 bg-gradient-to-l from-white via-white/95 to-transparent shadow-[-10px_0_16px_-14px_rgba(15,23,42,0.35)]" />
      )}

      <div
        ref={containerRef}
        role={ariaLabel ? 'region' : undefined}
        aria-label={ariaLabel}
        tabIndex={ariaLabel ? 0 : undefined}
        className={classes(
          'flex min-w-0 items-center overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          ariaLabel &&
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-secondary-500',
          contentClassName
        )}
      >
        {children}
      </div>
    </div>
  );
};
